import { Suspense, lazy, useEffect } from 'react';
import { initYandexMetrika } from './utils/metrika';

declare global {
  interface Window {
    ym: any;
  }
}
import { Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

import ProtectedRoute from './contexts/ProtectedRoute';
import OnboardingTour from './components/OnboardingTour/OnboardingTour';

const Landing = lazy(() => import('./pages/Landing/Landing'));
const Questionnaire = lazy(() => import('./pages/Questionnaire/Questionnaire'));
const Home = lazy(() => import('./pages/Home/Home'));
const Analysis = lazy(() => import('./pages/Analysis/Analysis'));
const Results = lazy(() => import('./pages/Results/Results'));
const Profile = lazy(() => import('./pages/Profile/Profile'));
const AnalysisStatus = lazy(() => import('./pages/AnalysisStatus/AnalysisStatus'));
const History = lazy(() => import('./pages/History/History'));
const Subscription = lazy(() => import('./pages/Subscription/Subscription'));
const LoginPage = lazy(() => import('./pages/LoginPage/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage/RegisterPage'));
const CompleteRegistration = lazy(
  () => import('./pages/CompleteRegistration/CompleteRegistration'),
);
const AnalysisSelection = lazy(
  () => import('./pages/AnalysisSelection/AnalysisSelection'),
);
const Preparation = lazy(() => import('./pages/Preparation/Preparation'));
const IdeaGenerator = lazy(() => import('./pages/IdeaGenerator/IdeaGenerator'));
const IdeasList = lazy(() => import('./pages/IdeasList/IdeasList'));
const ExpandedIdea = lazy(() => import('./pages/ExpandedIdea/ExpandedIdea'));
const Practice = lazy(() => import('./pages/Practice/Practice'));
const GeneratedSpeech = lazy(() => import('./pages/GeneratedSpeech/GeneratedSpeech'));
const SendRecording = lazy(() => import('./pages/SendRecording/SendRecording'));
const SpeechAnalysisStatus = lazy(
  () => import('./pages/SpeechAnalysisStatus/SpeechAnalysisStatus'),
);
const SpeechAnalysisResults = lazy(
  () => import('./pages/SpeechAnalysisResults/SpeechAnalysisResults'),
);
const HistoryDetail = lazy(() => import('./pages/HistoryDetail/HistoryDetail'));
const Feedback = lazy(() => import('./pages/Feedback/Feedback'));
const PolicyPage = lazy(() => import('./pages/Policies/PolicyPage'));
const AboutUs = lazy(() => import('./pages/About/AboutUs'));
const NotFound = lazy(() => import('./pages/NotFound/NotFound'));

function App() {
  const location = useLocation();

  useEffect(() => {
    const consent = localStorage.getItem('cookie_consent');
    if (consent === 'accepted') {
      initYandexMetrika();
      if (typeof window.ym === 'function') {
        window.ym(106409064, 'hit', location.pathname + location.search);
      }
    }
  }, [location]);

  return (
    <>
      <Toaster />
      <OnboardingTour />
      <Suspense fallback={<div className="app-loading">Loading...</div>}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/complete-registration" element={<CompleteRegistration />} />
          <Route
            path="/questionnaire"
            element={
              <ProtectedRoute>
                <Questionnaire />
              </ProtectedRoute>
            }
          />
          <Route
            path="/home"
            element={
              <ProtectedRoute>
                <Home />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analysis"
            element={
              <ProtectedRoute>
                <Analysis />
              </ProtectedRoute>
            }
          />
          <Route
            path="/results/:performance_id"
            element={
              <ProtectedRoute>
                <Results />
              </ProtectedRoute>
            }
          />
          <Route
            path="/generated-speech/:performance_id"
            element={
              <ProtectedRoute>
                <GeneratedSpeech />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analysis-status/:analysis_id"
            element={
              <ProtectedRoute>
                <AnalysisStatus />
              </ProtectedRoute>
            }
          />
          <Route
            path="/history"
            element={
              <ProtectedRoute>
                <History />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscription"
            element={
              <ProtectedRoute>
                <Subscription />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analysis-selection"
            element={
              <ProtectedRoute>
                <AnalysisSelection />
              </ProtectedRoute>
            }
          />
          <Route
            path="/preparation"
            element={
              <ProtectedRoute>
                <Preparation />
              </ProtectedRoute>
            }
          />
          <Route
            path="/idea-generator"
            element={
              <ProtectedRoute>
                <IdeaGenerator />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ideas-list/:survey_id"
            element={
              <ProtectedRoute>
                <IdeasList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/expanded-idea/:idea_id"
            element={
              <ProtectedRoute>
                <ExpandedIdea />
              </ProtectedRoute>
            }
          />
          <Route
            path="/practice/:performance_id"
            element={
              <ProtectedRoute>
                <Practice />
              </ProtectedRoute>
            }
          />
          <Route
            path="/send-recording/:performance_id"
            element={
              <ProtectedRoute>
                <SendRecording />
              </ProtectedRoute>
            }
          />
          <Route
            path="/speech-analysis-status/:analysis_id"
            element={
              <ProtectedRoute>
                <SpeechAnalysisStatus />
              </ProtectedRoute>
            }
          />
          <Route
            path="/speech-analysis-results/:analysis_id"
            element={
              <ProtectedRoute>
                <SpeechAnalysisResults />
              </ProtectedRoute>
            }
          />
          <Route
            path="/history/:history_id"
            element={
              <ProtectedRoute>
                <HistoryDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/feedback"
            element={
              <ProtectedRoute>
                <Feedback />
              </ProtectedRoute>
            }
          />
          <Route path="/docs/:docId" element={<PolicyPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default App;
